import { useForm } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { Button, Input, InputHelperText } from '@mint-lib/tw-components';
import { MediaPlan } from '@mint-sdk/api-media-plan';

const MAX_MEDIA_ROWS = 100;
const MIN_MEDIA_ROWS = 1;

type DuplicateMediaRowProps = {
  mediaData: MediaPlan.MediaData;
  closeMenu: () => void;
};

type FormData = {
  countInput: number;
};

const DuplicateMediaRow = ({
  mediaData,
  closeMenu,
}: DuplicateMediaRowProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      countInput: MIN_MEDIA_ROWS,
    },
  });

  const { mutateAsync: duplicate } = MediaPlan.useDuplicateMediaItem();

  const onSubmit = async (data: FormData) => {
    closeMenu();

    try {
      await duplicate({
        urlParams: { mediaItemId: mediaData.mediaItemId },
        data: {
          numberDuplicates: data.countInput,
          mediaDataId: mediaData.id,
        },
      });
    } catch {
      enqueueSnackbar({
        title: t('Failed to duplicate media row'),
        variant: 'error',
        removable: true,
      });
    }
  };

  return (
    <form className="w-74 px-3 py-1" onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-3 flex items-center justify-between">
        <h6 className="mb-0 text-sm text-text-secondary">
          {t('Duplicate media row')}
        </h6>
        <Button
          color="secondary"
          variant="contained"
          size="small"
          type="submit"
          aria-label="Duplicate media row submit"
          disabled={!isValid}
        >
          {t('Duplicate')}
        </Button>
      </div>
      <label className="flex flex-col text-xs text-text-secondary">
        <span>{t('New media rows amount')}</span>
        <Input
          className="w-full"
          size="small"
          type="number"
          {...register('countInput', {
            required: t('This field is required.'),
            min: {
              value: MIN_MEDIA_ROWS,
              message: t('Can not add less than {{min}} row', {
                min: MIN_MEDIA_ROWS,
              }),
            },
            max: {
              value: MAX_MEDIA_ROWS,
              message: t('Can not add more than {{max}} rows at once', {
                max: MAX_MEDIA_ROWS,
              }),
            },
            valueAsNumber: true,
          })}
        />
        {errors.countInput && (
          <InputHelperText
            helperText={errors.countInput.message || ''}
            color="error"
          />
        )}
      </label>
    </form>
  );
};

export default DuplicateMediaRow;
